import { createBrowserRouter, Navigate, type RouteObject } from 'react-router-dom';
import { AppShell } from '@/components/layout/AppShell';
import { RouteErrorBoundary } from '@/app/error/RouteErrorBoundary';
import { CalendarPage } from '@/features/calendar/CalendarPage';
import { DetailPanelRoute } from '@/features/initiative/DetailPanelRoute';
import { IntakePage } from '@/features/initiative/intake/IntakePage';
import { IntakeEditPage } from '@/features/initiative/intake/IntakeEditPage';
import { InitiativeListPage } from '@/features/initiative/list/InitiativeListPage';
import { PlaceholderPage } from '@/features/common/PlaceholderPage';
import { SalesCycleTimingPage } from '@/features/sales-cycle/SalesCycleTimingPage';
import { ExportCentrePage } from '@/features/exports/ExportCentrePage';
import { AdminPage } from '@/features/admin/AdminPage';
import { CapacityInputsPage } from '@/features/manpower/CapacityInputsPage';
import { CapacityUtilisationPage } from '@/features/manpower/CapacityUtilisationPage';
import { DemandExplorerPage } from '@/features/manpower/DemandExplorerPage';
import { EffortMatrixPage } from '@/features/manpower/EffortMatrixPage';

/**
 * The React Router 7 data router.
 *
 * The load-bearing decision is that the Detail_Panel is always a *child* of the
 * screen that lists Initiatives, never a sibling. Because the parent route stays
 * mounted when a child is active, that screen's filter, orientation, sort, page
 * and scroll state survive opening and closing the panel with no explicit
 * save/restore (R11.13); those states live in the URL search params, which the
 * parent reads. Two screens host it on those terms:
 *
 * - `/calendar/:year/initiative/:id` under `/calendar/:year`
 * - `/initiatives/:id` under `/initiatives`
 * - `/manpower/demand-explorer/initiative/:id` under `/manpower/demand-explorer`,
 *   whose breakdown rows are Initiatives when grouped by Initiative. That screen
 *   keeps its view state in component state rather than in the URL, so what the
 *   nesting preserves there is the state itself rather than the search params.
 *
 * Every route is wrapped by the shell and carries the error boundary that maps the
 * six error shapes. Routes whose feature work is a later task resolve to a titled
 * placeholder so navigation is never a dead link.
 */
/**
 * The route table, separate from {@link createAppRouter} so a test can mount it
 * under a memory router and exercise real navigation between screens - including
 * the two year-less redirects below, which no test could reach while the table
 * was inlined into a `createBrowserRouter` call.
 */
export function appRoutes(): RouteObject[] {
  return [
    {
      path: '/',
      element: <AppShell />,
      errorElement: <RouteErrorBoundary />,
      children: [
        { index: true, element: <CalendarYearRedirect /> },
        { path: 'calendar', element: <CalendarYearRedirect /> },
        {
          path: 'calendar/:year',
          element: <CalendarPage />,
          errorElement: <RouteErrorBoundary />,
          children: [
            {
              // Nested so the calendar parent stays mounted (R11.13).
              path: 'initiative/:id',
              element: <DetailPanelRoute />,
              errorElement: <RouteErrorBoundary />,
            },
          ],
        },
        {
          // The Initiative list screen: paginated, filtered, identifier-sorted,
          // and deliberately not year-scoped (see the page's own doc).
          path: 'initiatives',
          element: <InitiativeListPage />,
          errorElement: <RouteErrorBoundary />,
          children: [
            {
              // Nested for the same reason the calendar's panel is: the list's
              // filters, sort, page window and search all live in the search
              // params, and a nested child leaves the parent mounted, so opening
              // and closing the Detail_Panel cannot disturb them (R11.13).
              //
              // `:id` rather than `initiative/:id`: under `/initiatives` the extra
              // segment would only repeat the collection this id already belongs
              // to. The panel resolves its parent relatively, so both shapes work.
              path: ':id',
              element: <DetailPanelRoute />,
              errorElement: <RouteErrorBoundary />,
            },
          ],
        },
        {
          // Initiative_Intake_Form wizard (task 23.1).
          path: 'intake',
          element: <IntakePage />,
          errorElement: <RouteErrorBoundary />,
        },
        {
          // The same wizard mounted for an existing record, the Detail_Panel
          // footer's Edit action.
          path: 'initiative/:id/edit',
          element: <IntakeEditPage />,
          errorElement: <RouteErrorBoundary />,
        },
        {
          // Sales_Cycle_Timing_Form and impact list (task 23.3).
          path: 'sales-cycle-timing',
          element: <SalesCycleTimingPage />,
          errorElement: <RouteErrorBoundary />,
        },
        {
          // The Management_Dashboard (`complan-governance` R4.1), the Plan group's first
          // item. Code-split for the same measured reason as `/governance`: it mounts four
          // of the six Recharts visuals.
          path: 'dashboard',
          lazy: async () => {
            const { ManagementDashboardPage } = await import(
              '@/features/governance/ManagementDashboardPage'
            );
            return { Component: ManagementDashboardPage };
          },
          errorElement: <RouteErrorBoundary />,
        },
        {
          // The Governance_Dashboard (`complan-governance` R3.1). Registered in
          // `FILTER_SCREENS` as `/governance` before this route existed, because the
          // Drill_Through links built *from* it needed the descriptor either way.
          //
          // The one code-split route, and for a measured reason: it is the only screen
          // that imports Recharts, which with its d3 and state-management dependencies
          // is 119 kB gzipped - a third of the whole application bundle, paid for on
          // first paint by every user including those who never open a dashboard.
          // `lazy` rather than `React.lazy` because this is a data router, which
          // resolves the module as part of matching and therefore needs no `Suspense`
          // boundary and shows no fallback flash.
          path: 'governance',
          lazy: async () => {
            const { GovernanceDashboardPage } = await import(
              '@/features/governance/GovernanceDashboardPage'
            );
            return { Component: GovernanceDashboardPage };
          },
          errorElement: <RouteErrorBoundary />,
        },
        {
          // Export centre (task 23.5): full-backup and filtered-view Excel exports.
          path: 'reports',
          element: <ExportCentrePage />,
          errorElement: <RouteErrorBoundary />,
        },
        {
          // Master_Data_Administration (task 23.6): master data, status/finality,
          // validation rules, role assignment and operations, as capability-gated
          // tabs of the Administration screen.
          path: 'admin',
          element: <AdminPage />,
          errorElement: <RouteErrorBoundary />,
        },
        {
          // Manpower screens (task 11): Capacity & Utilisation, Demand Explorer,
          // Capacity Inputs, Effort Matrix.
          path: 'manpower/capacity-utilisation',
          element: <CapacityUtilisationPage />,
          errorElement: <RouteErrorBoundary />,
        },
        {
          path: 'manpower/demand-explorer',
          element: <DemandExplorerPage />,
          errorElement: <RouteErrorBoundary />,
          children: [
            {
              // The third host of the Detail_Panel, on the same terms as the other
              // two: grouped by Initiative, a breakdown row *is* an Initiative, and
              // nesting the panel leaves the explorer mounted so its grouping,
              // filters and planning year survive opening and closing it (R11.13).
              path: 'initiative/:id',
              element: <DetailPanelRoute />,
              errorElement: <RouteErrorBoundary />,
            },
          ],
        },
        {
          path: 'manpower/capacity-inputs',
          element: <CapacityInputsPage />,
          errorElement: <RouteErrorBoundary />,
        },
        {
          path: 'manpower/effort-matrix',
          element: <EffortMatrixPage />,
          errorElement: <RouteErrorBoundary />,
        },
        {
          path: '*',
          element: <PlaceholderPage titleKey="page.notFound.title" noteKey="page.notFound.note" />,
        },
      ],
    },
  ];
}

/**
 * The year-less entry points (`/` and `/calendar`, the latter being what the
 * sidebar's "Commercial Calendar" item links to) resolved to the current planning
 * year.
 *
 * A component rather than a `<Navigate>` built directly in the route table, so
 * the year is read when the redirect actually renders. `createAppRouter()` runs
 * once per application load and `App` memoises its result for the lifetime of the
 * tab, so a year captured while building the table would strand a long-lived tab
 * on the previous year's calendar after New Year.
 */
function CalendarYearRedirect(): React.JSX.Element {
  return <Navigate to={`/calendar/${String(new Date().getFullYear())}`} replace />;
}

export function createAppRouter(): ReturnType<typeof createBrowserRouter> {
  return createBrowserRouter(appRoutes());
}
